// @owlcat/app-shell — PURE purchase-widget logic (no React, no DOM). Kept separate so
// the compliance gate, currency pick, price format and entitlement-poll predicate are
// unit-testable without a renderer. Mirrors accounts/lib/currency so the price the
// widget SHOWS matches the currency the server CHARGES (resolved identically there).
/**
 * locale → the display/checkout currency when the price offers it. Mirrors accounts
 * lib/currency.LOCALE_TO_CURRENCY exactly (en → usd; ru/de/fr/cs/uk → eur).
 */
export const LOCALE_TO_CURRENCY = {
    en: "usd",
    ru: "eur",
    de: "eur",
    fr: "eur",
    cs: "eur",
    uk: "eur",
};
/** The currency used when a locale isn't mapped (mirrors accounts FALLBACK_CURRENCY). */
export const FALLBACK_CURRENCY = "eur";
/** Map a locale (or any language tag) to its default currency (lowercase). */
export function currencyForLocale(locale) {
    if (!locale)
        return FALLBACK_CURRENCY;
    const base = locale.toLowerCase().split("-")[0];
    return LOCALE_TO_CURRENCY[base] ?? FALLBACK_CURRENCY;
}
/**
 * The currency to DISPLAY a price in: the locale's preferred currency IF the map
 * offers it, otherwise the base. Identical rule to accounts resolveCheckoutCurrency,
 * so the shown price equals the charged price.
 */
export function pickDisplayCurrency(prices, base, locale) {
    const want = currencyForLocale(locale);
    return want in prices ? want : base.toLowerCase();
}
/** Format a minor-unit amount in a currency for display (Intl, graceful fallback). */
export function formatPrice(amountMinor, currency, locale) {
    const cur = currency.toUpperCase();
    try {
        return new Intl.NumberFormat(locale || undefined, {
            style: "currency",
            currency: cur,
        }).format(amountMinor / 100);
    }
    catch {
        return `${(amountMinor / 100).toFixed(2)} ${cur}`;
    }
}
/**
 * COMPLIANCE GATE (hard rule — Telegram ToS on digital goods): which payment methods
 * a purchase UI may render.
 *   • Inside a Telegram Mini App → card is FORBIDDEN (Telegram bans external payment
 *     providers for digital goods sold in-app). Stars ONLY. `card` is ALWAYS false.
 *   • In a plain browser → card iff a Stripe price exists; Stars iff a Stars amount.
 * There is NO input that can flip `card` on inside a TMA — the ban is unconditional.
 */
export function paymentMethods(input) {
    const stars = !!input.starsAmount && input.starsAmount > 0;
    if (input.inTma)
        return { card: false, stars };
    return { card: !!input.hasStripe, stars };
}
/**
 * Poll-success predicate for a SUBSCRIPTION purchase: the just-bought tier now shows
 * up in the identity's live activeTiers[] (the webhook has upserted the row). Case
 * sensitive — tiers are server-canonical.
 */
export function entitlementHasTier(ent, tier) {
    return !!ent?.activeTiers?.includes(tier);
}
/**
 * Poll-success predicate for a TOKEN TOP-UP: the wallet balance rose above the value
 * captured just before checkout (the webhook credit has landed).
 */
export function balanceIncreased(before, current) {
    return current > before;
}
export function planCtaState(input) {
    if (input.isCurrent)
        return "current";
    if (input.card || input.stars)
        return "purchasable";
    return "unavailable";
}
/**
 * A top-up pack as a share of the month (subscription-first fleet: tokens are hidden, the
 * sidebar shows a % ring). The month is the buyer's own plan — the largest `monthlyGrant` among
 * the tiers they hold — else the smallest paid plan of the app, the one they would compare
 * with. Whole percent, at least 1; null when no plan of the app says what a month is.
 */
export function packShareOfMonth(tokens, plans, activeTiers = []) {
    const grantOf = (p) => {
        const g = Number(p.metadata?.monthlyGrant);
        return Number.isFinite(g) && g > 0 ? g : null;
    };
    const held = plans.filter((p) => activeTiers.includes(p.tier)).map(grantOf).filter((g) => g !== null);
    const any = plans.map(grantOf).filter((g) => g !== null);
    const month = held.length ? Math.max(...held) : any.length ? Math.min(...any) : null;
    if (!month || !(tokens > 0))
        return null;
    return Math.max(1, Math.round((tokens / month) * 100));
}
